import { useState } from 'react';
import styled from 'styled-components';

import EditDischargeQuestionsModal from 'components/modals/EditDischargeQuestionsModal';
import Menu from 'components/shared/menu';
import { useTranslation } from 'context/translations';
import DischargeQuestions from 'models/activities/DischargeQuestions';
import Note from 'models/activities/Note';
import ProgressUpdate from 'models/activities/ProgressUpdate';
import Activity from 'models/Activity';
import Profile from 'models/Profile';
import { useUpdateActivity } from 'services/api/activity';
import { colors } from 'styles/theme/colors';
import CircleXIcon from 'svg/CircleXIcon';
import MenuHorizontalIcon from 'svg/MenuHorizontalIcon';
import PencilIcon from 'svg/PencilIcon';
import RestoreIcon from 'svg/RestoreIcon';

type Props = {
  activity: Activity;
  profile: Profile;
};

export function ActivityCardMenu({ activity, profile }: Props) {
  const [showEditDischargeModal, setShowEditDischargeModal] = useState(false);

  const { mutate } = useUpdateActivity();
  const { translate } = useTranslation();

  const toggleArchived = () => {
    mutate({
      id: activity.id,
      archived: !activity.archived,
      include: 'attachments.blob,attachments.fileDeletedByClient',
    });
  };

  const canDischarge = activity instanceof DischargeQuestions && (activity.ageInHours ?? 0) <= 72;
  const canRemove =
    activity.createdBy.id === profile.id &&
    !activity.escalated &&
    !!(activity.data.text?.length > 0 || activity.attachments.length) &&
    (activity instanceof Note || activity instanceof ProgressUpdate);

  if (!canDischarge && !canRemove) return <></>;

  return (
    <Container>
      <Menu>
        <Menu.Trigger>
          <MenuHorizontalIcon data-cy='editPatientCard' />
        </Menu.Trigger>
        <Menu.Content position='right'>
          <Menu.Item onClick={toggleArchived} visible={canRemove}>
            {activity.archived ? <RestoreIcon /> : <CircleXIcon color={colors.accentRed} width={18} height={18} />}
            {activity.archived ? 'Restore Note' : 'Remove Note'}
            {!!activity.attachments.length && ' & Attachments'}
          </Menu.Item>
          <Menu.Item onClick={() => setShowEditDischargeModal(true)} visible={canDischarge}>
            <PencilIcon width={18} height={18} />
            Edit {translate('Discharge')} Details
          </Menu.Item>
        </Menu.Content>
      </Menu>
      {activity instanceof DischargeQuestions && showEditDischargeModal && (
        <EditDischargeQuestionsModal activity={activity} setShow={setShowEditDischargeModal} />
      )}
    </Container>
  );
}

const Container = styled.div`
  margin-left: auto;
  width: 20px;
  position: relative;
`;
